import React, { useEffect, useState } from "react";
import { Modal, Tooltip, notification } from "antd";
import { useTranslation } from "react-i18next";
import authService from "services/authService";
import { updateStoredUser } from "utilities/authSession";

const AnimalAvatarPicker = ({ open, onClose, onSelected }) => {
  const { t } = useTranslation();
  const [animals, setAnimals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    authService
      .listAvatars()
      .then((result) => {
        if (!cancelled) setAnimals(result?.animals || []);
      })
      .catch(() => {
        if (!cancelled) {
          notification.error({ message: t("avatarLoadError") });
          setAnimals([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, t]);

  const handleSelect = async (animal) => {
    if (animal.taken || savingId) return;
    setSavingId(animal.id);
    try {
      const user = await authService.setAvatar(animal.id);
      updateStoredUser({
        animal_avatar: user?.animal_avatar || animal.id,
      });
      notification.success({ message: t("avatarSaveSuccess") });
      onSelected?.(user?.animal_avatar || animal.id);
      onClose?.();
    } catch (err) {
      const apiMessage =
        err?.response?.data?.message ||
        err?.response?.data?.result?.message ||
        err?.message ||
        "";
      const message = String(apiMessage).includes("ANIMAL_AVATAR_TAKEN")
        ? t("avatarTaken")
        : t("avatarSaveError");
      notification.error({ message });
      // Refresh availability after a conflict.
      try {
        const result = await authService.listAvatars();
        setAnimals(result?.animals || []);
      } catch (refreshErr) {
        // ignore refresh failure; toast already shown
      }
    } finally {
      setSavingId(null);
    }
  };

  return (
    <Modal
      title={t("chooseAvatar")}
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      className="animal-avatar-modal"
      width={420}
      destroyOnClose
      maskClosable={!savingId}
      closable={!savingId}
    >
      <p className="animal-avatar-hint">{t("chooseAvatarHint")}</p>
      <div className={`animal-avatar-grid ${loading ? "is-loading" : ""}`}>
        {animals.map((animal) => {
          const label = t(`animalAvatar_${animal.id}`, {
            defaultValue: animal.id,
          });
          const title = animal.taken
            ? t("avatarTakenBy", { name: animal.taken_by || t("operatorFallback") })
            : label;
          return (
            <Tooltip key={animal.id} title={title} placement="top">
              <button
                type="button"
                className={[
                  "animal-avatar-option",
                  animal.selected ? "is-selected" : "",
                  animal.taken ? "is-taken" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                disabled={animal.taken || Boolean(savingId)}
                aria-label={label}
                onClick={() => handleSelect(animal)}
              >
                <span className="animal-avatar-emoji" aria-hidden="true">
                  {animal.emoji}
                </span>
                <span className="animal-avatar-name">{label}</span>
              </button>
            </Tooltip>
          );
        })}
      </div>
    </Modal>
  );
};

export default AnimalAvatarPicker;
